/**
 * The guide registry — the one list every part of the system reads.
 *
 * Adding a guide is meant to be one entry here plus one content module. That
 * entry is then what drives, without any of them keeping their own copy of the
 * list:
 *
 *   - `vite.config.js`      the multi-page build input for its static document
 *   - `App.jsx`             the React route (one param route, not one per guide)
 *   - `scripts/build-guides.mjs`  the static document itself
 *   - `guides.seo.test.mjs` the head tags, structured data and link checks
 *
 * Three things still have to be added by hand, because each lives in a file
 * this module cannot reach — and each is asserted by the test, so forgetting one
 * fails the run rather than shipping a broken URL:
 *
 *   1. `vercel.json` (root AND landing-page/, kept identical) — a rewrite from
 *      the clean URL to the document, plus the two redirects. The catch-all
 *      `"/(.*)" -> "/"` swallows any path that has no rewrite above it.
 *   2. `public/sitemap.xml` — the clean URL.
 *   3. `src/hooks/useAnalyticsContext.js` — the page name, or every CTA on the
 *      route reports itself as the homepage.
 *
 * Every guide carries a Greek counterpart as `el`. The corpus is bilingual in
 * full — fourteen identities, fourteen English modules, fourteen Greek ones —
 * so `guideServedLanguage` never falls back in practice today. The fallback
 * stays, and stays tested, because it is what makes adding a fifteenth guide
 * safe: an English module can ship on its own and acquire its Greek later
 * without any URL in this file being wrong in the meantime.
 *
 * The Greek modules are ADAPTATIONS rather than translations. Three of them
 * differ from their English source in ways worth knowing about before editing
 * either half:
 *
 *   - `wedding-seating-chart-vs-seating-plan` resolves an ambiguity between two
 *     ENGLISH phrases. Greek has no such pair, so the Greek article answers the
 *     distinction Greek couples actually confuse — the working plan versus the
 *     board guests read at the entrance.
 *   - `how-to-collect-wedding-photos-from-guests` and `wedding-qr-code-for-photos`
 *     have overlapping Greek search intent, so the Greek pair is split by
 *     PURPOSE: the first owns the decision, the second owns the mechanism.
 *     Neither may take the other's half back.
 *   - `how-to-create-wedding-budget` does not answer «πόσο κοστίζει ένας γάμος»
 *     and carries no Greek cost figures, because none we could publish would be
 *     real.
 *
 * ── The slug is per language ──────────────────────────────────────────────
 * `entry.slug` is the guide's IDENTITY and its English slug. A translated guide
 * gets its own slug in its own language, because a Greek URL carrying an
 * English keyword is a Greek page that cannot rank for the query it was written
 * for:
 *
 *   /guides/wedding-seating-plan   ↔   /el/guides/plano-trapezion-gamou
 *
 * That slug is NOT a second field to keep in step. It lives where the rest of a
 * guide's identity already lives — in the content module, next to its title and
 * description — and `guideSlug` reads it from there. A Greek article has a
 * Greek title, a Greek description and a Greek slug; splitting the last one
 * into the registry would be the only part of a guide's identity held anywhere
 * other than the guide.
 *
 * `guideSlug`, `guideServedLanguage` and `guideHref` below are the only three
 * functions that answer "which URL", and every renderer, generator, build input
 * and test goes through them.
 */

import { DEFAULT_LANGUAGE, LANGUAGES } from "../../lib/language.js";
import { guidePath } from "./guideModel.js";

import weddingSeatingPlanEn from "./wedding-seating-plan.en.js";
import weddingSeatingPlanEl from "./wedding-seating-plan.el.js";
import howManyPeopleWeddingTableEn from "./how-many-people-wedding-table.en.js";
import howManyPeopleWeddingTableEl from "./how-many-people-wedding-table.el.js";
import weddingSeatingChartVsSeatingPlanEn from "./wedding-seating-chart-vs-seating-plan.en.js";
import weddingSeatingChartVsSeatingPlanEl from "./wedding-seating-chart-vs-seating-plan.el.js";
import whenShouldWeddingGuestsRsvpEn from "./when-should-wedding-guests-rsvp.en.js";
import whenShouldWeddingGuestsRsvpEl from "./when-should-wedding-guests-rsvp.el.js";
import howToOrganizeWeddingRsvpsEn from "./how-to-organize-wedding-rsvps.en.js";
import howToOrganizeWeddingRsvpsEl from "./how-to-organize-wedding-rsvps.el.js";
import howToCollectWeddingPhotosFromGuestsEn from "./how-to-collect-wedding-photos-from-guests.en.js";
import howToCollectWeddingPhotosFromGuestsEl from "./how-to-collect-wedding-photos-from-guests.el.js";
import weddingQrCodeForPhotosEn from "./wedding-qr-code-for-photos.en.js";
import weddingQrCodeForPhotosEl from "./wedding-qr-code-for-photos.el.js";
import howToOrganizeWeddingGuestListEn from "./how-to-organize-wedding-guest-list.en.js";
import howToOrganizeWeddingGuestListEl from "./how-to-organize-wedding-guest-list.el.js";
import weddingPlanningChecklistEn from "./wedding-planning-checklist.en.js";
import weddingPlanningChecklistEl from "./wedding-planning-checklist.el.js";
import howToTrackWeddingVendorsPaymentsEn from "./how-to-track-wedding-vendors-payments.en.js";
import howToTrackWeddingVendorsPaymentsEl from "./how-to-track-wedding-vendors-payments.el.js";
import howToCreateWeddingBudgetEn from "./how-to-create-wedding-budget.en.js";
import howToCreateWeddingBudgetEl from "./how-to-create-wedding-budget.el.js";
import weddingWebsiteVsDigitalWeddingInvitationEn from "./wedding-website-vs-digital-wedding-invitation.en.js";
import weddingWebsiteVsDigitalWeddingInvitationEl from "./wedding-website-vs-digital-wedding-invitation.el.js";
import whatToIncludeDigitalWeddingInvitationEn from "./what-to-include-digital-wedding-invitation.en.js";
import whatToIncludeDigitalWeddingInvitationEl from "./what-to-include-digital-wedding-invitation.el.js";
import weddingPlanningSpreadsheetWhatToTrackEn from "./wedding-planning-spreadsheet-what-to-track.en.js";
import weddingPlanningSpreadsheetWhatToTrackEl from "./wedding-planning-spreadsheet-what-to-track.el.js";

export const GUIDES = [
  {
    slug: "wedding-seating-plan",
    en: weddingSeatingPlanEn,
    el: weddingSeatingPlanEl,
  },
  {
    slug: "how-many-people-wedding-table",
    en: howManyPeopleWeddingTableEn,
    el: howManyPeopleWeddingTableEl,
  },
  {
    slug: "wedding-seating-chart-vs-seating-plan",
    en: weddingSeatingChartVsSeatingPlanEn,
    el: weddingSeatingChartVsSeatingPlanEl,
  },
  {
    slug: "when-should-wedding-guests-rsvp",
    en: whenShouldWeddingGuestsRsvpEn,
    el: whenShouldWeddingGuestsRsvpEl,
  },
  {
    slug: "how-to-organize-wedding-rsvps",
    en: howToOrganizeWeddingRsvpsEn,
    el: howToOrganizeWeddingRsvpsEl,
  },
  {
    slug: "how-to-collect-wedding-photos-from-guests",
    en: howToCollectWeddingPhotosFromGuestsEn,
    el: howToCollectWeddingPhotosFromGuestsEl,
  },
  {
    slug: "wedding-qr-code-for-photos",
    en: weddingQrCodeForPhotosEn,
    el: weddingQrCodeForPhotosEl,
  },
  {
    slug: "how-to-organize-wedding-guest-list",
    en: howToOrganizeWeddingGuestListEn,
    el: howToOrganizeWeddingGuestListEl,
  },
  {
    slug: "wedding-planning-checklist",
    en: weddingPlanningChecklistEn,
    el: weddingPlanningChecklistEl,
  },
  {
    slug: "how-to-track-wedding-vendors-payments",
    en: howToTrackWeddingVendorsPaymentsEn,
    el: howToTrackWeddingVendorsPaymentsEl,
  },
  {
    slug: "how-to-create-wedding-budget",
    en: howToCreateWeddingBudgetEn,
    el: howToCreateWeddingBudgetEl,
  },
  {
    slug: "wedding-website-vs-digital-wedding-invitation",
    en: weddingWebsiteVsDigitalWeddingInvitationEn,
    el: weddingWebsiteVsDigitalWeddingInvitationEl,
  },
  {
    slug: "what-to-include-digital-wedding-invitation",
    en: whatToIncludeDigitalWeddingInvitationEn,
    el: whatToIncludeDigitalWeddingInvitationEl,
  },
  {
    slug: "wedding-planning-spreadsheet-what-to-track",
    en: weddingPlanningSpreadsheetWhatToTrackEn,
    el: weddingPlanningSpreadsheetWhatToTrackEl,
  },
];

export const GUIDE_SLUGS = GUIDES.map((guide) => guide.slug);

/**
 * The content module to render for a language. Falls back to English, which is
 * what the static document carries and what every guide is written in first.
 */
export const guideContentFor = (entry, language) =>
  (language === "el" && entry.el) || entry.en;

/**
 * The language a guide is actually SERVED in when a reader wants `language`.
 *
 * The same fallback `guideContentFor` applies, stated separately because the
 * two answers must not diverge: a reader with Greek selected who opens an
 * English-only guide is shown English, so that guide's canonical, its URL and
 * its `<html lang>` are English too. Rendering English copy at a Greek URL —
 * or canonicalising it to an `/el/` address that has no document — is how a
 * translation that was never written becomes an indexed 404.
 */
export const guideServedLanguage = (entry, language = DEFAULT_LANGUAGE) =>
  entry?.[language] ? language : DEFAULT_LANGUAGE;

/**
 * The slug a guide's URL uses in a language — its own, where it has one.
 *
 * Read from the content module, which already declares it. `guideServedLanguage`
 * first, so asking for a language a guide is not written in returns the English
 * slug rather than minting a Greek URL for an English article.
 */
export const guideSlug = (entry, language = DEFAULT_LANGUAGE) =>
  entry?.[guideServedLanguage(entry, language)]?.slug ?? entry?.slug;

/**
 * The href a reader in `language` should be sent to for this guide — the one
 * function every link to a guide goes through.
 *
 * Slug and prefix are resolved together, and that is the point. Resolving them
 * apart is how you get `/el/guides/<english-slug>`: the right prefix on the
 * wrong slug, pointing at a document that does not exist. Both renderers, the
 * index, the route and the generator call this.
 */
export const guideHref = (entry, language = DEFAULT_LANGUAGE) =>
  guidePath(guideSlug(entry, language), guideServedLanguage(entry, language));

/**
 * The registry entry for a slug, or undefined.
 *
 * Matches the identity slug OR any language's slug, because the route resolves
 * whatever the URL actually carried: `/el/guides/plano-trapezion-gamou` arrives
 * as `plano-trapezion-gamou` and has to find the same entry that
 * `/guides/wedding-seating-plan` finds. One guide, two addresses, one lookup.
 */
export const findGuideEntry = (slug) =>
  GUIDES.find(
    (guide) =>
      guide.slug === slug ||
      LANGUAGES.some((language) => guide[language]?.slug === slug),
  );

/**
 * The languages a guide is actually written in — the content modules it has,
 * not the languages the site supports.
 *
 * This is what makes English-only, Greek-only and bilingual guides all
 * expressible without a special case, and it is the single fact the page
 * registry, the generator, the build inputs and the sitemap check all read. A
 * guide gets a Greek URL when, and only when, it has a Greek module.
 */
export const guideLanguages = (entry) =>
  LANGUAGES.filter((language) => Boolean(entry[language]));

/**
 * The static document for a guide, relative to Vite's root (the landing-page
 * directory). Used by `vite.config.js` for its build inputs and by the
 * generator for its output path, so the two can never disagree about where a
 * guide's document lives.
 *
 * The document path mirrors the URL: `/guides/x` is served by `guides/x.html`,
 * and `/el/guides/y` by `el/guides/y.html`. Keeping the two in the same shape
 * is what lets one rewrite rule per guide per language stay mechanical.
 *
 * Takes the slug ALREADY RESOLVED for the language — `guideSlug(entry, lang)`,
 * not `entry.slug`. These two helpers stay pure string work so the document
 * path and the URL are built by the same rule from the same input; passing the
 * identity slug with a non-default language would name a file that the rewrite
 * for that URL does not point at.
 */
export const guideDocumentPath = (slug, language = DEFAULT_LANGUAGE) =>
  language === DEFAULT_LANGUAGE
    ? `guides/${slug}.html`
    : `${language}/guides/${slug}.html`;

/** The Vite rollup input name for a guide's document. */
export const guideEntryName = (slug, language = DEFAULT_LANGUAGE) =>
  (language === DEFAULT_LANGUAGE ? `guide-${slug}` : `guide-${language}-${slug}`)
    .replace(/[^a-zA-Z0-9-]/g, "-");
